/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { OrderDetailsTableStyle } from "@eisinc/App/SharedStyles/OrderDetailsStyles";
import { screen } from "@eisinc/XDL/Helpers";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { OrderLineModel, ShipmentPackageLineDto } from "@insite/client-framework/Types/ApiModels";
import DataTable from "@insite/mobius/DataTable/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    orderLines: OrderLineModel[];
    orderLineShipments: ShipmentPackageLineDto[];
}

export interface OrderDetailsShipmentDetailsProps {
    tableHeaderStyle: DataTableHeaderProps;
    tableRowStyle: DataTableRowProps;
    tableCellStyle: DataTableCellProps;
}

export const OrderDetailsShipmentDetailsInfoStyles: OrderDetailsShipmentDetailsProps = {
    tableRowStyle: {
        evenRowCss: css`
            &&& {
                background: transparent;
            }
        `,
        css: css`
            background: transparent;
            border-color: ${getColor("common.ui4")};
            &&&:hover,
            &&&:focus {
                background: transparent;
                cursor: unset;
            }
            @media print {
                &&& {
                    border: 0;
                    display: flex;
                    flex-direction: column;
                }
            }
            ${screen("lte", "md")`
                display: flex;
                flex-direction: column;
                padding: 20px 0;
                border-top-width: 1px;
                border-top-style: solid;
                td {
                    height: 100%;
                    padding: 6px 0;
                }
             `}
        `,
    },
    tableHeaderStyle: {
        typographyProps: {
            ...OrderDetailsTableStyle.tableHeaderStyle.typographyProps,
        },
        css: css`
            ${OrderDetailsTableStyle.tableHeaderStyle.css}
            border-color: ${getColor("common.ui4")};
        `,
    },
    tableCellStyle: {
        css: css`
            ${OrderDetailsTableStyle.tableCellStyle.css}
            height: 40px;
        `,
    },
};

type Props = OwnProps;

const OrderDetailsShipmentInfoDetails: FC<Props> = ({ orderLines, orderLineShipments }) => {
    const headerColumns = [`${translate("Product #")}`, `${translate("Qty Shipped")}`, `${translate("UOM")}`];
    const styles = mergeToNew(OrderDetailsTableStyle, OrderDetailsShipmentDetailsInfoStyles);

    return (
        <DataTable
            css={css`
                width: 100%;
            `}
        >
            <DataTableHead {...styles.shippingDetailsTableHeaderStyle}>
                {headerColumns.map((column, index) => (
                    <DataTableHeader key={`${column}_${index}`} {...styles.tableHeaderStyle}>
                        {column}
                    </DataTableHeader>
                ))}
            </DataTableHead>
            <DataTableBody>
                {orderLineShipments.map((orderLineShipment: any) => {
                    return (
                        <DataTableRow key={orderLineShipment.id} {...styles.tableRowStyle}>
                            <DataTableCell {...styles.tableCellStyle}>
                                <Hidden {...styles.hiddenHeaderStyle}>
                                    <GridItem {...styles.tableCellGridStyle}>{headerColumns[0]}</GridItem>
                                </Hidden>
                                <GridItem {...styles.tableCellGridStyle}>
                                    {orderLines[orderLineShipment.orderLineId]?.productErpNumber || "– –"}
                                </GridItem>
                            </DataTableCell>
                            <DataTableCell {...styles.tableCellStyle}>
                                <Hidden {...styles.hiddenHeaderStyle}>
                                    <GridItem {...styles.tableCellGridStyle}>{headerColumns[1]}</GridItem>
                                </Hidden>
                                <GridItem {...styles.tableCellGridStyle}>{orderLineShipment.qtyShipped}</GridItem>
                            </DataTableCell>
                            <DataTableCell {...styles.tableCellStyle}>
                                <Hidden {...styles.hiddenHeaderStyle}>
                                    <GridItem {...styles.tableCellGridStyle}>{headerColumns[2]}</GridItem>
                                </Hidden>
                                <GridItem {...styles.tableCellGridStyle}>
                                    {orderLines[orderLineShipment.orderLineId]?.unitOfMeasure || "– –"}
                                </GridItem>
                            </DataTableCell>
                        </DataTableRow>
                    );
                })}
            </DataTableBody>
        </DataTable>
    );
};

export default OrderDetailsShipmentInfoDetails;
